// [DELETION-PIN] Hook pour gérer la suppression avec re-tentative après PIN
import { useState, useCallback } from "react";
import axios from "axios";

/**
 * useDeletionPin: Wraps a delete callback so a PIN dialog appears when
 * the backend responds with 403 PIN_REQUIRED. The dialog submits the PIN
 * as X-Deletion-Pin header and retries the same delete.
 *
 * @param {(pin?: string) => Promise<void>} deleteFn  Function that performs the DELETE.
 *                                                    Receives an optional pin, must include it
 *                                                    as X-Deletion-Pin header.
 */
export function useDeletionPin(deleteFn) {
  const [open, setOpen] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const runDelete = useCallback(async (pin) => {
    setError(null);
    setLoading(true);
    try {
      await deleteFn(pin);
      setOpen(false);
      return { ok: true };
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const status = err?.response?.status;
      if (status === 403 && detail === "PIN_REQUIRED") {
        setError(pin ? "Code PIN incorrect" : null);
        setOpen(true);
        return { ok: false, needPin: true };
      }
      throw err;
    } finally {
      setLoading(false);
    }
  }, [deleteFn]);

  const submitPin = useCallback((pin) => runDelete(pin), [runDelete]);

  const cancel = useCallback(() => {
    setOpen(false);
    setError(null);
  }, []);

  return {
    open,
    error,
    loading,
    runDelete,       // Trigger initial attempt (no PIN)
    submitPin,       // Called by dialog on submit
    cancel,
    setOpen,
  };
}
